import { useMemo } from 'react';
import { useBetslipContext } from '../contexts/BetslipContext';
import { useGlobalTicketConditions } from '../queries';
import useSelectionsBonus from './useSelectionsBonus';
import { WALLET_TYPE, WalletType } from '../types/enums';

type UseBetslipBonusArgs = {
  maxPayout: number;
  isSystemTicket: boolean;
  selectedWallet: WalletType;
};

export const useBetslipBonus = ({ maxPayout, isSystemTicket, selectedWallet }: UseBetslipBonusArgs) => {
  const { isWaysTicket, bets, betslipTicketType } = useBetslipContext();
  const { data: globalTicketConditions } = useGlobalTicketConditions();

  const isBonusWallet = selectedWallet === WALLET_TYPE.BONUS;

  const skipBonusAmountCalculation = useMemo(
    () =>
      isWaysTicket ||
      isSystemTicket ||
      (bets.some((bet) => Number(bet.singlesStakeAmount) > 0) && bets.length > 1),
    [isWaysTicket, isSystemTicket, bets]
  );

  const { bonusAmount, bonusPercentage, hasActiveCampaigns, eligibility } = useSelectionsBonus(
    maxPayout,
    skipBonusAmountCalculation
  );

  const showBonus = !!eligibility && !skipBonusAmountCalculation;

  const totalWinningAmountWithBonus = Math.min(
    maxPayout + bonusAmount,
    globalTicketConditions?.[selectedWallet]?.[betslipTicketType].maxWinning || Infinity
  );

  const totalWinningAmount = showBonus ? totalWinningAmountWithBonus : maxPayout;

  const { nextBonusPercentage = 0, selectionsNeeded = 0, minRequiredOdd } = eligibility || {};

  return {
    bonusAmount,
    bonusPercentage,
    totalWinningAmount,
    showBonus,
    hasActiveCampaigns,
    isBonusWallet,
    showAddMoreSelectionsMsg: selectionsNeeded > 0,
    showBonusNotAvailableMsg: skipBonusAmountCalculation,
    eligibility: {
      nextBonusPercentage,
      selectionsNeeded,
      minRequiredOdd,
    },
  };
};
